import config from '../config'

export interface GpuDeviceStatus {
  index: number
  util_percent?: number | null
  temp_c?: number | null
  mem_used_mb?: number | null
  mem_total_mb?: number | null
}

export interface GpuStatus {
  available: boolean
  gpus: GpuDeviceStatus[]
}

export interface DiskStatus {
  path: string
  used_bytes: number
  total_bytes: number
}

// 오브젝트 스토리지 데이터 버킷 합계. 용량 상한이 없어 total 이 없다.
export interface ObjectStorageStatus {
  used_bytes: number
  object_count: number
  measured_at: string
}

export interface MemoryStatus {
  used_bytes: number
  total_bytes: number
}

export interface SystemStatusResponse {
  cpu_percent: number
  memory: MemoryStatus
  // 저장소가 오브젝트 스토리지(S3)면 마운트가 없어 null — 대신 storage 에 버킷 사용량
  disk: DiskStatus | null
  storage?: ObjectStorageStatus | null
  gpu: GpuStatus
  updated_at: string
}

class SystemService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async getStatus(): Promise<SystemStatusResponse> {
    const resp = await fetch(`${this.baseUrl}/system/status`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      throw new Error(`시스템 상태 조회 실패: ${resp.status}`)
    }
    return resp.json()
  }

  private triggerLabelCache: TriggerLabelSchemas | null = null

  async getTriggerLabels(): Promise<TriggerLabelSchemas> {
    if (this.triggerLabelCache) return this.triggerLabelCache
    const resp = await fetch(`${this.baseUrl}/system/trigger-labels`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) return {}
    const data = await resp.json()
    this.triggerLabelCache = data
    return data
  }

  /**
   * trigger_id에서 prefix와 condition을 파싱하여 display_name을 반환합니다.
   * 예: TRG_FLD_PRD_ACT → prefix=TRG, category=FLD, condition=ACT → "ACT(동작)"
   */
  resolveTriggerDisplayName(
    schemas: TriggerLabelSchemas,
    triggerId?: string | null,
  ): { categoryLabel?: string; conditionLabel?: string } {
    if (!triggerId) return {}
    const parts = triggerId.split('_').filter(Boolean)
    const prefix = parts[0] || ''
    const category = parts[1] || ''
    const condition = parts.length >= 4 ? parts[3] : parts[2] || ''

    const schema = schemas[prefix]
    if (!schema) return {}

    const categoryCfg = schema.categories?.[category]
    if (!categoryCfg) return {}

    const categoryLabel = categoryCfg.display_name || categoryCfg.label || category
    const conditionCfg = categoryCfg.conditions?.[condition]
    let conditionLabel: string | undefined
    if (typeof conditionCfg === 'object') {
      conditionLabel = conditionCfg.display_name || conditionCfg.label
    } else if (typeof conditionCfg === 'string') {
      conditionLabel = conditionCfg
    }

    return { categoryLabel, conditionLabel: conditionLabel || condition }
  }
}

export interface TriggerLabelSchemas {
  [prefix: string]: {
    rule_name: string
    prefix: string
    categories: {
      [code: string]: {
        label?: string
        display_name?: string
        conditions?: {
          [code: string]: { label?: string; display_name?: string } | string
        }
      }
    }
  }
}

export default new SystemService()


